import { toast, type ExternalToast } from 'sonner';

import { EDITOR_HUD_TOP } from './editor-chrome';

/**
 * Id of the <Toaster> mounted INSIDE the panorama editor's viewport.
 *
 * Editor feedback ("copiado", "pegado", "deshecho", save/delete errors) belongs
 * next to the thing being edited, not in the app's global top-right corner,
 * where it covers "Ver como cliente" / "Publicar" and the panel header. Sonner
 * routes a toast to the Toaster whose `id` matches its `toasterId`; everything
 * else keeps using the global one mounted in main.tsx.
 */
export const EDITOR_TOASTER_ID = 'panorama-editor';

/**
 * Top offset (px) of the editor Toaster, measured from the viewport top: the
 * toasts stack at the top-right, just below the tool top bar row and the HD
 * indicator (both start at `EDITOR_HUD_TOP`, under the floating top bar, and
 * are ~36–44px tall), so they never cover them, the dock or the bottom bar.
 */
export const EDITOR_TOASTER_TOP_OFFSET = EDITOR_HUD_TOP + 48;

/** Gap between the tool top bars and the first toast, when the bars run lower
 *  than `EDITOR_TOASTER_TOP_OFFSET` (a bar that wrapped, two stacked bars). */
const TOASTER_GAP_BELOW_BARS = 12;

/** The Toaster's top offset for a top zone ending at `barsBottom` px. */
export function editorToasterTop(barsBottom: number): number {
  return Math.max(EDITOR_TOASTER_TOP_OFFSET, barsBottom + TOASTER_GAP_BELOW_BARS);
}

// Many panel components, hooks and libs report through `editorToast` but are
// also reused outside the editor (tour list, tour settings). A toast aimed at
// a Toaster that is not mounted is silently dropped, so the editor registers
// its Toaster here and `editorToast` falls back to the global one otherwise.
let mountedEditorToasters = 0;

/** Called by the editor while its Toaster is mounted. Returns the cleanup. */
export function registerEditorToaster(): () => void {
  mountedEditorToasters += 1;
  return () => {
    mountedEditorToasters = Math.max(0, mountedEditorToasters - 1);
  };
}

const SHORT_MS = 2000;
/** A toast with an action ("Deshacer") needs time to be read and clicked. */
const ACTION_MS = 5000;

const opts = (o: ExternalToast | undefined, short: boolean): ExternalToast => {
  const base: ExternalToast = short ? { duration: o?.action ? ACTION_MS : SHORT_MS } : {};
  return mountedEditorToasters > 0
    ? { ...base, ...o, toasterId: EDITOR_TOASTER_ID }
    : { ...base, ...o };
};

/**
 * `toast` scoped to the editor viewport (global Toaster outside the editor).
 * Successes and infos last ~2s; errors keep sonner's default.
 */
export const editorToast = {
  success: (message: string, o?: ExternalToast) => toast.success(message, opts(o, true)),
  error: (message: string, o?: ExternalToast) => toast.error(message, opts(o, false)),
  info: (message: string, o?: ExternalToast) => toast.info(message, opts(o, true)),
  warning: (message: string, o?: ExternalToast) => toast.warning(message, opts(o, false)),
  /** Long-running work; the loading toast stays until the promise settles. */
  promise: <T>(promise: Parameters<typeof toast.promise<T>>[0], data: Parameters<typeof toast.promise<T>>[1]) =>
    toast.promise<T>(promise, { ...data, ...(mountedEditorToasters > 0 ? { toasterId: EDITOR_TOASTER_ID } : {}) }),
};
